import * as FluxUtils from 'flux/utils';
import * as MobileDetect from 'mobile-detect';

import { DispatcherBaseAction, AppDispatcher } from '../dispatcher/AppDispatcher';
import { getEnvVar } from '../submodules/utils/ReactScriptHelper';
import { KompanBase } from '../Kompan/KompanBase';

import utils = egr.wcf.utils;

declare var WindowsAppMessageSender: any;

type ShareFormat = 'Mail' | 'Download' | 'Formular' | 'App';

interface ShareOption {
    type: ShareFormat;
    disabled: boolean;
}

export interface DownloadOption {
    type: ExportFormat;
    visible: boolean;
    disabled: boolean;
}

export interface AppConfigurationStoreState {
    noWebGL: boolean;

    eaiws_keepalive: number | undefined;

    singleHamburgerTabs: boolean;
    propertyImageFormats: Array<'svg' | 'png' | 'jpg'>;

    os: string;
    iDevice: boolean;
    appVersion: string | null;
    mobileDevice: boolean;
    touchDevice: boolean;

    dataLanguageFilter: Array<string>;

    cadDownloadTypes: Array<DownloadOption>;
    articleListTypes: Array<DownloadOption>;
    commercialDownloadTypes: Array<DownloadOption>;
    shareTypes: Array<ShareOption>;
    allowPrices: boolean;
    pricesNeedLogin: boolean;
    customFloors: boolean;
    floorplanAvailable: boolean;
    letterBarInCatalog: boolean;
    persistentProjects: boolean;
    sidebarMoveable: boolean;
    fileBasedProjects: boolean;
    showArticleNumbers: boolean;

    twoStepSettingSaving: boolean;
}

export interface EaiwsManualSetup {
    type: 'manual';

    server: string;
    startup: string;
}

export interface EaiwsGatekeeperSetup {
    type: 'gatekeeper';

    id: string;
}

export abstract class AppConfiguration {
    public static readonly AllowedLanguages: Array<string> = ['de', 'en', 'es', 'fr', 'it', 'nl', 'pl', 'pt', 'ro', 'tr'];
    public static readonly FallbackLanguage: string = 'en';
    public static readonly DEFAUL_CURRENCY_MAP: Map<string, number> = new Map<string, number>([
        ['JPY', 113.072], ['AOA', 181.533], ['TWD', 34.883], ['MAD', 11.125], ['USD', 1.089],
        ['CAD', 1.453], ['INR', 72.942], ['HKD', 8.447], ['OMR', 0.42], ['PLN', 4.338],
        ['KRW', 1255.78], ['RUB', 67.898], ['LVL', 0.703], ['MKD', 61.914], ['HUF', 309.47],
        ['SGD', 1.519], ['EGP', 9.69], ['RON', 4.515], ['BND', 1.543], ['ARS', 16.61],
        ['EUR', 1], ['DKK', 7.44], ['NZD', 1.522], ['TND', 2.465], ['ZAR', 15.253],
        ['DZD', 120.816], ['CHF', 1.082], ['CLP', 734.513], ['NOK', 9.005],
        ['KWD', 0.33], ['LBP', 1684.95], ['SYP', 233.948], ['ILS', 4.215], ['IDR', 14215.3],
        ['CZK', 27.067], ['MYR', 4.558], ['COP', 3219.69], ['LYD', 1.556], ['SAR', 4.089],
        ['QAR', 3.968], ['GBP', 0.891], ['AUD', 1.432], ['CNY', 7.366], ['VND', 24720],
        ['PHP', 52.656], ['THB', 38.428], ['TRY', 3.361], ['BRL', 3.434], ['UYU', 31.187],
        ['YER', 272.985], ['SEK', 9.729], ['BHD', 0.413], ['HRK', 7.514], ['MXN', 20.275],
        ['AED', 4.001], ['JOD', 0.773], ['UAH', 28.246]
    ]);
}

class AppConfigurationStoreClass extends FluxUtils.ReduceStore<AppConfigurationStoreState, unknown> {
    // private static isBox: boolean = true; // Set to false for pCon.planner

    public dataLanguageFilter = (value: string): boolean => {
        const state: AppConfigurationStoreState = this.getState();
        return !(
            state.dataLanguageFilter.length &&
            state.dataLanguageFilter.indexOf(value) === -1
        );
    };

    public getValue(parameter: string, defaultValue: string | null): string | null {
        return utils.string.getUrlParamValue(window.location.href, parameter) || defaultValue;
    }

    /**
     * Checks if an soft-keyboard is shown for input-elements,
     * and the device/app does not move the input-element above the showing keyboard
     * @return {boolean}
     */
    public softKeyboard(): boolean {
        const state: AppConfigurationStoreState = this.getState();
        return (
            (
                state.os === 'AndroidOS'
            ) &&
            state.touchDevice
        );
    }

    public getEaiwsSetup(usePackageGroups: boolean = false): EaiwsManualSetup | EaiwsGatekeeperSetup {
        const server: string | null = this.getValue('eaiws_baseurl', KompanBase.getEAIWSUrl());
        const startup: string | null = this.getValue('eaiws_startup', KompanBase.Configuration.DistributionRegion);
        const gatekeeperId: string | null = this.getValue('GATEKEEPER_ID', null);

        if (utils.string.isNotNullOrEmpty(server) && utils.string.isNotNullOrEmpty(startup)) {
            return {
                type: 'manual',
                server,
                startup
            };
        }
        if (utils.string.isNotNullOrEmpty(gatekeeperId)) {
            return {
                type: 'gatekeeper',
                id: gatekeeperId
            };
        }

        const idWithLogin: string = getEnvVar('GATEKEEPER_ID_WITH_LOGIN', '');
        const idWithoutLogin: string = getEnvVar('GATEKEEPER_ID_WITHOUT_LOGIN', '');
        return {
            type: 'gatekeeper',
            id: (usePackageGroups ? idWithLogin : idWithoutLogin)
        };
    }

    public shareFormatAvailable(shareFormat: ShareFormat): boolean {
        const state: AppConfigurationStoreState = this.getState();
        return state.shareTypes.filter(
            (shareOption) => { return (shareOption.type === shareFormat && shareOption.disabled === false); }
        ).length > 0;
    }

    public getInitialState(): AppConfigurationStoreState {
        const windowsApp: boolean = typeof WindowsAppMessageSender !== 'undefined';

        const tMobileDetect: MobileDetect = new MobileDetect(window.navigator.userAgent);

        let appVersion: string | null = null;
        try {
            if (windowsApp) {
                appVersion = WindowsAppMessageSender.getVersion();
            } else {
                const match: RegExpMatchArray | null = navigator.userAgent.match(/pCon\.box\/([0-9\.]+)/i);
                if (match != null && !utils.string.isNullOrEmpty(match[1])) {
                    appVersion = match[1];
                }
            }
        } catch (_) {
            // ignore errors
        }

        return {
            noWebGL: !BABYLON.Engine.isSupported(),
            eaiws_keepalive: 60000,
            singleHamburgerTabs: true,
            propertyImageFormats: ['svg', 'png', 'jpg'],
            // Device-informations
            os: tMobileDetect.os(),
            appVersion,
            touchDevice: ('ontouchstart' in window || navigator.maxTouchPoints) as boolean,
            mobileDevice: (tMobileDetect.mobile() != null),
            iDevice: (tMobileDetect.is('iPhone') || tMobileDetect.is('iPad') || tMobileDetect.is('iPod')),
            // Filter
            dataLanguageFilter: [], // empty array == no filter
            // Features
            allowPrices: true,
            pricesNeedLogin: true,
            cadDownloadTypes: [
                {type: 'DWG', visible: true, disabled: true},
                {type: '3DS', visible: true, disabled: true},
                {type: 'FBX', visible: true, disabled: true},
                {type: 'SKP', visible: true, disabled: true},
            ],
            articleListTypes: [
                {type: 'PDF', visible: true, disabled: true},
                {type: 'EXCEL', visible: true, disabled: true},
            ],
            commercialDownloadTypes: [
                {type: 'OBX', visible: true, disabled: true},
                {type: 'OBK', visible: true, disabled: true},
                {type: 'GAEB', visible: true, disabled: true}
            ],
            shareTypes: [
                {type: 'Mail', disabled: true},
                {type: 'Download', disabled: true},
                {type: 'Formular', disabled: true},
                {type: 'App', disabled: true}
            ],
            customFloors: true, /* colors for solid floor */
            floorplanAvailable: true,
            letterBarInCatalog: true,
            persistentProjects: true,
            sidebarMoveable: true,
            fileBasedProjects: true,
            showArticleNumbers: true,
            // Customize
            twoStepSettingSaving: true,
        };
    }

    public reduce(state: AppConfigurationStoreState, action: DispatcherBaseAction): AppConfigurationStoreState {
        // Important: the store state has to be static and changes will break the app!
        return state;
    }
}

export let AppConfigurationStore: AppConfigurationStoreClass = new AppConfigurationStoreClass(AppDispatcher);


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/stores/AppConfigurationStore.ts